import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock, DollarSign, Download, FileText, Printer, Truck, Warehouse } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useReactToPrint } from 'react-to-print';
import { api } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { fmtCurrency } from '../../lib/display-labels';
import { formatCalendarDate } from '../../lib/calendar-date';
import {
  crewDetailExport,
  detailDateText,
  detailHoursText,
  detailRefText,
  detailShowsPay,
  fieldHoursSourceLabel,
  hrs,
  readCrewHoursDetail,
  type CrewDetailRow,
  type CrewDetailSection,
  type CrewHoursDetail,
} from '../../lib/reports/crew-hours';
import { useBackToList } from '../downhole/recordPage';

/**
 * ONE EMPLOYEE'S HOURS & JOBS — the page an employee row on the Hours & Jobs
 * report opens (TNT request; api#1193). Its own route with the band's back
 * arrow, never a drawer, modal or split pane (CLAUDE.md). Jobs, then Shop,
 * each with its subtotal, then the grand total.
 *
 * The range comes from the URL (`?from=&to=`), which the report writes, so it
 * carries over; the back arrow returns to the report as it was left
 * (`useBackToList`). Changing a date here rewrites this page's URL only.
 *
 * EVERY FIGURE IS THE API'S — subtotals and the grand total included; nothing
 * on this page adds a column up. Two of the API's totals are NARROWER than the
 * rows above them, and the page says so rather than letting a reader add the
 * column and find a different number:
 *   - hours leave out a multi-day ticket (no per-day hours), and
 *   - pay leaves out a row with no pay yet (unpriced, not yet paid).
 * The second count is a tally of the rows on this page, which is honest
 * because the endpoint is unpaginated: every row it has is here.
 */

const money = (v: number | null | undefined) => fmtCurrency(v ?? null);

/** The band's secondary action: white/25 edge, no colour class (CLAUDE.md). */
const BAND_SECONDARY =
  'inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-white/25 px-3 text-xs font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50';

function rangeText(from: string | null, to: string | null): string {
  const f = formatCalendarDate(from, { month: 'short', day: 'numeric', year: 'numeric' });
  const t = formatCalendarDate(to, { month: 'short', day: 'numeric', year: 'numeric' });
  return f && t ? `${f} – ${t}` : '—';
}

function filename(d: CrewHoursDetail | undefined, from: string, to: string): string {
  const name = (d?.employeeName ?? 'Employee').replace(/[^a-zA-Z0-9]+/g, '_');
  return `Hours_and_Jobs_${name}_${from}_to_${to}`;
}

export default function EmployeeHoursDetailScreen() {
  const { userId = '' } = useParams<{ userId: string }>();
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const location = useLocation();
  const back = useBackToList('/reports');
  const printRef = useRef<HTMLDivElement>(null);

  const query = useQuery({
    queryKey: ['report-crew-detail', userId, from, to],
    queryFn: async () => readCrewHoursDetail((await api.reports.crewHoursDetail(userId, { dateFrom: from, dateTo: to })).data),
    // A cold link with no range is not a request for all time.
    enabled: !!userId && !!from && !!to,
    refetchOnWindowFocus: false,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const d = query.data;
  const failure = queryLoadFailure(query);
  const loading = query.isLoading && !failure;
  const showPay = d ? detailShowsPay(d) : false;

  const setDate = (key: 'from' | 'to', value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    // Replace, and keep the location state: it holds the report's URL, which
    // is where the back arrow goes.
    setParams(next, { replace: true, state: location.state });
  };

  const exportTable = useMemo(() => (d ? crewDetailExport(d) : null), [d]);
  const sheet = useCallback((bookType: 'csv' | 'xlsx') => {
    if (!exportTable) return;
    const ws = XLSX.utils.aoa_to_sheet([exportTable.headers, ...exportTable.rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hours');
    XLSX.writeFile(wb, `${filename(d, from, to)}.${bookType}`, { bookType });
    toast.success(bookType === 'csv' ? 'CSV downloaded' : 'Excel downloaded');
  }, [exportTable, d, from, to]);
  const pdf = useCallback(() => {
    if (!exportTable || !d) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text(`${d.employeeName ?? 'Employee'} — Hours & Jobs`, 14, 18);
    doc.setFontSize(10);
    doc.text(rangeText(d.from, d.to), 14, 26);
    autoTable(doc, {
      startY: 32,
      head: [exportTable.headers],
      body: exportTable.rows,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255 },
    });
    doc.save(`${filename(d, from, to)}.pdf`);
    toast.success('PDF downloaded');
  }, [exportTable, d, from, to]);
  const print = useReactToPrint({ contentRef: printRef, documentTitle: filename(d, from, to) });

  const ready = !!d && !failure;
  const actions = (
    <div className="flex flex-wrap gap-2">
      <button type="button" className={BAND_SECONDARY} onClick={pdf} disabled={!ready}><FileText size={14} aria-hidden="true" /> PDF</button>
      <button type="button" className={BAND_SECONDARY} onClick={() => sheet('csv')} disabled={!ready}><Download size={14} aria-hidden="true" /> CSV</button>
      <button type="button" className={BAND_SECONDARY} onClick={() => sheet('xlsx')} disabled={!ready}><Download size={14} aria-hidden="true" /> Excel</button>
      <button type="button" className={BAND_SECONDARY} onClick={() => print()} disabled={!ready}><Printer size={14} aria-hidden="true" /> Print</button>
    </div>
  );

  const stats = [
    { icon: <Truck size={16} />, label: 'Field hrs', value: d ? hrs(d.field.hours) : '—', sub: (d && fieldHoursSourceLabel(d.fieldHoursSource)) ?? undefined, loading },
    { icon: <Warehouse size={16} />, label: 'Shop hrs', value: d ? hrs(d.shop.hours) : '—', sub: 'Approved shifts', loading },
    { icon: <Clock size={16} />, label: 'Total hrs', value: d ? hrs(d.totalHours) : '—', sub: 'Field + shop', loading },
    ...(showPay || loading
      ? [{ icon: <DollarSign size={16} />, label: 'Pay', value: d ? money(d.totalPay) : '—', sub: 'Excl. OT & adj.', loading }]
      : []),
  ];

  return (
    <div data-testid="employee-hours-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`Hours & Jobs · ${rangeText(from || null, to || null)}`}
        title={d?.employeeName ?? (loading ? 'Loading…' : 'Employee')}
        onBack={back}
        backLabel="Back to Hours & Jobs by Employee"
        actions={actions}
        stats={stats}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-[11px] font-medium text-fieldiq-muted">
              <span className="mb-1.5 block">Start date</span>
              <input type="date" value={from} onChange={(e) => setDate('from', e.target.value)}
                className="h-9 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 text-xs text-fieldiq-text focus:outline-none focus:ring-1 focus:ring-fieldiq-accent" />
            </label>
            <label className="text-[11px] font-medium text-fieldiq-muted">
              <span className="mb-1.5 block">End date</span>
              <input type="date" value={to} onChange={(e) => setDate('to', e.target.value)}
                className="h-9 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 text-xs text-fieldiq-text focus:outline-none focus:ring-1 focus:ring-fieldiq-accent" />
            </label>
          </div>

          {(!from || !to) ? (
            <p className="text-sm text-fieldiq-muted">Pick a start and end date to see this employee's hours.</p>
          ) : failure ? (
            <LoadFailureNotice error={queryFailureError(query)} subject="this employee's hours" allowed="admins and office staff" />
          ) : loading || !d ? (
            <p className="text-sm text-fieldiq-muted">Loading…</p>
          ) : (
            <div ref={printRef} className="space-y-4">
              <Section
                testId="employee-hours-jobs"
                eyebrow={fieldHoursSourceLabel(d.fieldHoursSource) ?? 'Field'}
                title="Jobs"
                section={d.field}
                detail={d}
                showPay={showPay}
                empty="No field work in this range."
                notes={[
                  d.unresolvedTicketCount ? `${d.unresolvedTicketCount} multi-day ${d.unresolvedTicketCount === 1 ? 'ticket has' : 'tickets have'} no per-day hours, so ${d.unresolvedTicketCount === 1 ? 'it is' : 'they are'} not in the hours subtotal.` : null,
                ]}
              />
              <Section
                testId="employee-hours-shop"
                eyebrow="Approved shifts"
                title="Shop"
                section={d.shop}
                detail={d}
                showPay={showPay}
                empty="No approved shop shifts in this range."
                notes={[
                  d.unapprovedShopHours ? `${hrs(d.unapprovedShopHours)} of shop time awaiting approval is not counted.` : null,
                ]}
              />
              <GrandTotal detail={d} showPay={showPay} />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Section({ testId, eyebrow, title, section, detail, showPay, empty, notes }: {
  testId: string;
  eyebrow: string;
  title: string;
  section: CrewDetailSection;
  detail: CrewHoursDetail;
  showPay: boolean;
  empty: string;
  notes: (string | null)[];
}) {
  const unpaid = showPay ? section.rows.filter((r) => r.pay === null).length : 0;
  const footer = [
    ...notes,
    unpaid > 0 ? `${unpaid} ${unpaid === 1 ? 'row has' : 'rows have'} no pay yet and ${unpaid === 1 ? 'is' : 'are'} not in the pay subtotal.` : null,
  ].filter(Boolean) as string[];
  return (
    <section data-testid={testId} className="overflow-hidden rounded-fi-panel border border-l-4 border-fieldiq-border border-l-fi-amber bg-fieldiq-card">
      <header className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">{title}</h2>
      </header>
      {section.rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-fieldiq-muted">{empty}</p>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <tr>
                  <th scope="col" className="px-4 py-2.5">Date</th>
                  <th scope="col" className="px-4 py-2.5">{title === 'Shop' ? 'Clock in – out' : 'Ticket / shift'}</th>
                  <th scope="col" className="px-4 py-2.5">Job</th>
                  <th scope="col" className="px-4 py-2.5">Customer</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Hours</th>
                  <th scope="col" className="px-4 py-2.5">Note</th>
                  {showPay && <th scope="col" className="px-4 py-2.5 text-right">Pay</th>}
                </tr>
              </thead>
              <tbody>
                {section.rows.map((r, i) => <DetailRow key={i} r={r} detail={detail} showPay={showPay} />)}
                <tr data-testid={`${testId}-subtotal`} className="border-t-2 border-fieldiq-border bg-fieldiq-dark/30 font-semibold text-fieldiq-text">
                  <td className="px-4 py-2.5" colSpan={4}>
                    Subtotal{section.count !== null ? ` · ${section.count} ${section.count === 1 ? 'row' : 'rows'}` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{hrs(section.hours)}</td>
                  <td />
                  {showPay && <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{money(section.pay)}</td>}
                </tr>
              </tbody>
            </table>
          </div>
          <ul className="md:hidden divide-y divide-fieldiq-border">
            {section.rows.map((r, i) => <DetailCard key={i} r={r} detail={detail} showPay={showPay} />)}
            <li data-testid={`${testId}-subtotal-card`} className="flex justify-between bg-fieldiq-dark/30 px-4 py-2.5 text-sm font-semibold text-fieldiq-text">
              <span>Subtotal{section.count !== null ? ` · ${section.count}` : ''}</span>
              <span className="tabular-nums">{hrs(section.hours)}{showPay ? ` · ${money(section.pay)}` : ''}</span>
            </li>
          </ul>
        </>
      )}
      {footer.length > 0 && (
        <footer className="border-t border-fieldiq-border px-4 py-2.5 text-[11px] text-fieldiq-muted">
          {footer.map((f) => <p key={f}>{f}</p>)}
        </footer>
      )}
    </section>
  );
}

function noteOf(r: CrewDetailRow, showPay: boolean): string | null {
  return [r.hoursReason, showPay && r.pay === null ? r.payReason : null].filter(Boolean).join(' · ') || null;
}

function DetailRow({ r, detail, showPay }: { r: CrewDetailRow; detail: CrewHoursDetail; showPay: boolean }) {
  return (
    <tr data-testid="employee-hours-row" className="border-t border-fieldiq-border text-fieldiq-text">
      <td className="px-4 py-2.5 whitespace-nowrap">{detailDateText(r)}</td>
      <td className="px-4 py-2.5 whitespace-nowrap tabular-nums font-semibold">{detailRefText(r, detail.timeZone)}</td>
      <td className="px-4 py-2.5 whitespace-nowrap">{r.jobNumber ?? '—'}</td>
      <td className="px-4 py-2.5">{r.customerName ?? '—'}</td>
      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{detailHoursText(r, money)}</td>
      <td className="px-4 py-2.5 max-w-[320px] text-xs text-fieldiq-muted">{noteOf(r, showPay) ?? ''}</td>
      {showPay && <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">{money(r.pay)}</td>}
    </tr>
  );
}

function DetailCard({ r, detail, showPay }: { r: CrewDetailRow; detail: CrewHoursDetail; showPay: boolean }) {
  const note = noteOf(r, showPay);
  return (
    <li data-testid="employee-hours-card" className="px-4 py-2.5 text-sm text-fieldiq-text">
      <div className="flex justify-between gap-3">
        <span className="font-semibold tabular-nums">{detailRefText(r, detail.timeZone)}</span>
        <span className="tabular-nums whitespace-nowrap">{detailHoursText(r, money)}</span>
      </div>
      <div className="mt-0.5 flex justify-between gap-3 text-xs text-fieldiq-muted">
        <span>{[detailDateText(r), r.jobNumber, r.customerName].filter(Boolean).join(' · ')}</span>
        {showPay && <span className="tabular-nums text-fieldiq-text">{money(r.pay)}</span>}
      </div>
      {note && <p className="mt-0.5 text-xs text-fieldiq-muted">{note}</p>}
    </li>
  );
}

function GrandTotal({ detail, showPay }: { detail: CrewHoursDetail; showPay: boolean }) {
  return (
    <section data-testid="employee-hours-total" className="rounded-fi-panel border border-l-4 border-fieldiq-border border-l-fi-amber bg-fieldiq-card px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-sm font-extrabold uppercase tracking-wide text-fieldiq-text">Grand total</h2>
        <p className="text-base font-extrabold tabular-nums text-fieldiq-text">
          {hrs(detail.totalHours)}{showPay ? ` · ${money(detail.totalPay)}` : ''}
        </p>
      </div>
      <p className="mt-1 text-[11px] text-fieldiq-muted">
        Field + shop{detail.jobsWorked !== null ? ` · ${detail.jobsWorked} ${detail.jobsWorked === 1 ? 'job' : 'jobs'}` : ''}{detail.daysWorked !== null ? ` · ${detail.daysWorked} ${detail.daysWorked === 1 ? 'day' : 'days'}` : ''}
        {showPay && detail.payNote ? ` · Pay ${detail.payNote.charAt(0).toLowerCase()}${detail.payNote.slice(1)}` : ''}
      </p>
    </section>
  );
}
